import { h, defineComponent, ref, watch } from 'vue';
import { d, getLocale, t } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import { box, indexView, loader, pagination, tag } from '@/ui/components';
import { bulkDelete, list as getChecklistList, remove } from '@/services/api/resources/checklist';
import { emptyList } from '@/services/api/response/types';
import { ChecklistList, ChecklistRecord, Status } from '@/entities/checklist';
import { Task, usePagination, useSorting, useTask } from '@/ui/concerns';
import { filter as getAllCategories } from '@/services/api/resources/category';
import { hasFeature, hasInspectionQuotaLeft, hasPermission, isOnFreePlan, Permissions } from '@/ui/auth/auth';
import { dataTitleProp, filterListToOptions, sequence } from '@/ui/helpers/uiHelper';
import { FilterTypes, trackFilter } from '@/services/activityTracker/activityTrackerHelper';
import { column, tagColumn } from '@/ui/components/table/TableColumn';
import { checklistStatus } from '@/ui/dictionary';
import {
  assigneeFilter,
  categoryFilter,
  checklistStatusFilter,
  searchFilter,
  entityItemFilter,
} from '@/ui/components/filters/filters-horizontal';
import { TagType } from '@/ui/components/Tag';
import { SelectOptions, SortDirection } from '@/ui/types';
import { checklistIndexActions } from '@/ui/pages/checklist/ChecklistIndexActions';
import { useChecklistCheck } from '@/ui/pages/checklist/useChecklistCheck';
import { filterFormLoader } from '@/ui/components/Loader';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { TourTypes, useIntercomTour } from '@/ui/pages/useIntercomTour';
import { Tooltip } from '@/ui/components/Tooltip';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { bulkDeleteButton } from '@/ui/components/Button';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { Repeat } from '@/entities/rrule/constants';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { updateAdDropdown } from '@/ui/components/upgrade/UpgradeAdDropdown';
import ChecklistEmpty from '@/ui/pages/checklist/ChecklistEmpty.vue';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { SortingIcon } from '@/ui/components/icon/SortingIcon';
import { ActivityModal } from '@/ui/components/modals/ActivityModal';
import { InformationModal } from '@/ui/components/modals/InformationModal';
import { router } from '@/ui/routes';

export const ChecklistIndexTab = defineComponent({
  name: 'ChecklistIndexTab',
  setup() {
    if (!hasFeature('inspections')) {
      return renderResourceTeaserPage(t('common.inspections'));
    }

    if (!hasPermission(Permissions.MANAGE_CHECKLISTS) && !hasPermission(Permissions.CONDUCT_OPEN_INSPECTIONS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const showEditAccessDeniedModal = ref(false);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir, onSortChange } = useSorting('createdAt', SortDirection.Descending);
    const { checkResult, performCheck } = useChecklistCheck();

    const withTour = qs.boolean('withTour', false);
    const { showTour } = useIntercomTour();
    const currentPageName = 'Templates Index';

    const search = qs.string('q', '');
    watch(search, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        trackFilter(FilterTypes.search, { page: currentPageName });
      }
    });

    const category = qs.numbers('category', []);
    watch(category, (newVal, oldVal) => {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        trackFilter(FilterTypes.category, { page: currentPageName });
      }
    });

    const status = qs.nullableNumber('status', null);
    watch(status, (newVal, oldVal) => {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        trackFilter(FilterTypes.status, { page: currentPageName });
      }
    });

    const entityItem = qs.numbers('entityItem', []);
    watch(entityItem, (newVal, oldVal) => {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        trackFilter(FilterTypes.entityItem, { page: currentPageName });
      }
    });

    const conductibleBy = qs.numbers('conductibleBy', []);
    watch(conductibleBy, (newVal, oldVal) => {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        trackFilter(FilterTypes.assignee, { page: currentPageName });
      }
    });

    // get filters task
    const categories = ref<SelectOptions>([]);
    const entityItems = ref<SelectOptions>([]);
    const users = ref<SelectOptions>([]);
    let getFilters: Task<unknown>;
    if (hasFeature('analytics')) {
      getFilters = useTask(
        () => sequence(getAllCategories(), getAllEntityItems(), getAllUsers()),
        ([categoryList, entityItemList, userList]) => {
          categories.value = filterListToOptions(categoryList);
          entityItems.value = filterListToOptions(entityItemList);
          users.value = filterListToOptions(userList);
        },
      );
    }

    // get list task
    const list = ref<ChecklistList>(emptyList());
    const getList = useTask(
      () => getChecklistList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        category: category.value,
        sort: sort.value,
        dir: dir.value,
        status: status.value,
        entityItem: entityItem.value,
        conductibleBy: conductibleBy.value,
      }),
      listResponse => {
        list.value = listResponse;

        if (withTour.value) {
          showTour(TourTypes.checklist, getLocale());
        }
      },
    );

    watch([
      page,
      perPage,
      search,
      category,
      status,
      entityItem,
      conductibleBy,
      sort,
      dir,
    ], getList.run);

    watch(() => getList.state.value.pending, newVal => {
      initialLoading.value = newVal;
    });

    const statusToTagType: Record<Status, TagType> = {
      [Status.Inactive]: 'error',
      [Status.Active]: 'success',
    };

    const statusTag = (s: Status) => tag(
      statusToTagType[s],
      checklistStatus(s),
    );

    const { selectedResources, deleteBulkTask, onCheckedRowsUpdate, isBulkDeletionDisabled } = useBulkDeletable({
      getList,
      bulkDeleteFn: bulkDelete,
    });

    const getScheduleText = (checklist: ChecklistRecord): string | null => {
      if (!checklist.data.schedules || !checklist.data.schedules.length) {
        return t('checklist.not-scheduled');
      }

      if (checklist.data.schedules.length > 1) {
        return `${checklist.data.schedules.length} ${t('common.schedules')}`;
      }

      if (checklist.data.schedules[0].data.title) {
        return checklist.data.schedules[0].data.title;
      }

      if (checklist.data.schedules[0].data.rule.repeat !== Repeat.NoRepeat) {
        return t(`checklist.${checklist.data.schedules[0].data.rule.repeat}`);
      }

      return checklist.data.schedules[0].data.rule.start
        ? d(checklist.data.schedules[0].data.rule.start)
        : null;
    };

    const selectedChecklistForActivities = ref<ChecklistRecord | null>(null);

    const handleActivityModal = (checkList?: ChecklistRecord) => {
      selectedChecklistForActivities.value = checkList || null;
    };

    return function render() {
      const upgradeMessage = h(UpgradeAd, {
        class: 'upgrade-ad--checklist-index',
        props: {
          message: t('upgrade-ad.checklist-index'),
          withDropdown: false,
          placeholder: t('common.categories'),
        },
      });

      return loader('div', performCheck, [
        selectedChecklistForActivities.value ? h(ActivityModal, {
          props: {
            active: !!selectedChecklistForActivities.value,
            id: selectedChecklistForActivities.value.id,
            logType: 'checklists',
            title: t('activity-logs.checklist-title', { title: selectedChecklistForActivities.value?.data.title }),
          },
          on: {
            close: () => handleActivityModal(),
          },
        }) : null,
        h(InformationModal, {
          props: {
            active: !!showEditAccessDeniedModal.value,
            confirmText: t('common.ok'),
          },
          on: {
            close: () => { showEditAccessDeniedModal.value = false; },
          },
        }, [t('checklist.access-denied')]),
        checkResult.value
          ? indexView([
            isOnFreePlan.value && upgradeMessage,
            filterFormLoader(getFilters, [
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search, resetPagination),
              isOnFreePlan.value
                ? updateAdDropdown({
                  props: {
                    placeholder: t('common.categories'),
                    classes: 'field is-horizontal mr-4',
                  },
                })
                : categoryFilter(category, categories.value, resetPagination),
              checklistStatusFilter(status, resetPagination),
              entityItemFilter(entityItem, entityItems.value, resetPagination),
              assigneeFilter(conductibleBy, users.value, resetPagination),
            ], true, isOnFreePlan.value ? 'pt-2' : ''),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    permissionName: 'update',
                    editRoute: 'checklistEdit',
                    list: list.value.data,
                    deleteFn: remove,
                    sort: sort.value,
                    dir: dir.value,
                    selectedRecords: selectedResources.value,
                    loading: deleteBulkTask.state.value.pending,
                    paginationLinks: list.value.meta,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('common.templates'),
                      singular: t('common.template'),
                    },
                    sortableTitle: true,
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                    'needs-refresh-content': getList.run,
                    click: (row: ChecklistRecord) => {
                      if (row.authorizedTo.update) {
                        router.push({
                          name: 'checklistEdit',
                          params: { id: row.id.toString() },
                        });

                        return;
                      }
                      if (row.authorizedTo.conduct) {
                        router.push({
                          name: 'inspectionCreate',
                          params: { id: row.id.toString() },
                        });

                        return;
                      }

                      showEditAccessDeniedModal.value = true;
                    },
                    sort: (field: string, direction: SortDirection) => {
                      onSortChange(field, direction);
                      resetPagination();
                    },
                  },
                  scopedSlots: {
                    title: ({ row: checklist }: { row: ChecklistRecord }) =>
                      checklist.data.title || t('checklist.no-title'),
                    default: () => [
                      tagColumn({
                        field: 'categories',
                        label: t('common.categories'),
                      }, {
                        default: ({ row: checklist }: { row: ChecklistRecord }) =>
                          h('div', checklist.data.categories.map(dataTitleProp).join(',')),
                      }),
                      column({
                        field: 'schedules',
                        label: t('common.schedules'),
                      }, {
                        default: ({ row: checklist }: { row: ChecklistRecord }) => getScheduleText(checklist),
                        header: () => h('div', { class: 'page-title-tooltiped' }, [
                          t('common.schedules'),
                          h(Tooltip, {
                            props: {
                              label: t('feature-explanation.schedules'),
                              multilined: true,
                              position: 'is-bottom',
                            },
                            slot: 'header',
                          }, [
                            h('div', [
                              featureExplanationIcon(),
                            ]),
                          ]),
                        ]),
                      }),
                      column({
                        field: 'status',
                        label: t('common.status'),
                        sortable: true,
                      }, {
                        default: ({ row: checklist }: { row: ChecklistRecord }) => statusTag(checklist.data.status),
                        header: () => h('div', { class: 'is-relative', slot: 'header' }, [
                          t('common.status'),
                          h(Tooltip, {
                            props: {
                              label: t('feature-explanation.checklist-status'),
                              multilined: true,
                              position: 'is-bottom',
                            },
                          }, [
                            h('div', [
                              featureExplanationIcon(),
                            ]),
                          ]),
                          h(SortingIcon, {
                            props: {
                              field: 'status',
                              sort: sort.value,
                              direction: dir.value,
                            },
                          }),
                        ]),
                      }),
                    ],
                    beforeDeleteButton: checklistIndexActions(hasInspectionQuotaLeft.value, handleActivityModal),
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ])
          : box([h(ChecklistEmpty)]),
      ], false, 'checklist-index-page');
    };
  },
});
